import { useState, useEffect } from 'react';
import {
  IonButton, IonInput, IonTextarea, IonSelect,
  IonSelectOption, IonLabel, IonIcon, IonToast, IonSpinner,
} from '@ionic/react';
import { save } from 'ionicons/icons';
import { z } from 'zod';
import { workTypeEnum, PROVINCES } from '@/lib/validation/registration';
import { useJob, useUpdateJobListing } from '@/lib/api/hooks';

const editSchema = z.object({
  title: z.string().min(5, 'Title must be at least 5 characters'),
  description: z.string().min(20, 'Description must be at least 20 characters'),
  province: z.string().min(1, 'Select a province'),
  location: z.string().min(2, 'Enter a location'),
  salaryMin: z.number().min(0).optional(),
  salaryMax: z.number().min(0).optional(),
  workType: z.enum(workTypeEnum),
});

interface JobEditForm {
  title: string;
  description: string;
  requirements: string;
  skillsRequired: string;
  province: string;
  location: string;
  salaryMin: string;
  salaryMax: string;
  workType: string;
  applicationDeadline: string;
}

interface JobEditPanelProps {
  jobId: string;
  onSaved?: () => void;
  onCancel?: () => void;
}

export function JobEditPanel({ jobId, onSaved, onCancel }: JobEditPanelProps) {
  const { data: jobEnvelope, isLoading: loadingJob } = useJob(jobId);
  const job = jobEnvelope?.data;
  const updateJob = useUpdateJobListing();

  const [form, setForm] = useState<JobEditForm>({
    title: '',
    description: '',
    requirements: '',
    skillsRequired: '',
    province: '',
    location: '',
    salaryMin: '',
    salaryMax: '',
    workType: 'Contract',
    applicationDeadline: '',
  });

  const [initialized, setInitialized] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ msg: string; color: string } | null>(null);

  useEffect(() => {
    if (job && !initialized) {
      setForm({
        title: job.title ?? '',
        description: job.description ?? '',
        requirements: (job.requirements ?? []).join('\n'),
        skillsRequired: (job.skillsRequired ?? []).join(', '),
        province: job.province ?? '',
        location: job.location ?? '',
        salaryMin: job.salaryMin != null ? String(job.salaryMin) : '',
        salaryMax: job.salaryMax != null ? String(job.salaryMax) : '',
        workType: job.workType ?? 'Contract',
        applicationDeadline: job.applicationDeadline ?? '',
      });
      setInitialized(true);
    }
  }, [job, initialized]);

  const set = (patch: Partial<JobEditForm>) => setForm((f) => ({ ...f, ...patch }));

  const handleSave = () => {
    const parsed = editSchema.safeParse({
      title: form.title,
      description: form.description,
      province: form.province,
      location: form.location,
      salaryMin: form.salaryMin ? Number(form.salaryMin) : undefined,
      salaryMax: form.salaryMax ? Number(form.salaryMax) : undefined,
      workType: form.workType,
    });
    if (!parsed.success) {
      const e: Record<string, string> = {};
      for (const issue of parsed.error.issues) e[String(issue.path[0])] = issue.message;
      setErrors(e);
      return;
    }
    setErrors({});

    updateJob.mutate(
      {
        id: jobId,
        data: {
          title: form.title,
          description: form.description,
          requirements: form.requirements ? form.requirements.split('\n').filter(Boolean) : [],
          skillsRequired: form.skillsRequired ? form.skillsRequired.split(',').map((s) => s.trim()).filter(Boolean) : [],
          province: form.province,
          location: form.location,
          salaryMin: form.salaryMin ? Number(form.salaryMin) : undefined,
          salaryMax: form.salaryMax ? Number(form.salaryMax) : undefined,
          workType: form.workType as 'Permanent' | 'Contract' | 'PartTime' | 'Freelance',
          applicationDeadline: form.applicationDeadline || undefined,
        },
      },
      {
        onSuccess: () => {
          setToast({ msg: 'Job updated', color: 'success' });
          onSaved?.();
        },
        onError: (err: Error) => {
          setToast({ msg: err.message || 'Failed to update job', color: 'danger' });
        },
      },
    );
  };

  if (loadingJob) {
    return <div className="ion-text-center ion-padding"><IonSpinner /></div>;
  }

  if (!job) {
    return <p className="bp-subheading ion-padding">Job not found.</p>;
  }

  return (
    <div className="ion-padding">
      <IonLabel style={{ fontWeight: 600, fontSize: '1.1rem' }}>Edit job listing</IonLabel>

      <div style={{ marginTop: 16 }}>
        <IonInput
          label="Job title"
          placeholder="e.g. Emergency Electrician"
          value={form.title}
          onIonInput={(e) => set({ title: e.detail.value! })}
        />
        {errors.title && <IonLabel color="danger" className="ion-padding-start">{errors.title}</IonLabel>}

        <IonTextarea
          label="Description"
          placeholder="Describe the role, responsibilities, and requirements"
          value={form.description}
          onIonInput={(e) => set({ description: e.detail.value! })}
          rows={4}
          style={{ marginTop: 12 }}
        />
        {errors.description && <IonLabel color="danger" className="ion-padding-start">{errors.description}</IonLabel>}

        <IonTextarea
          label="Requirements (one per line)"
          value={form.requirements}
          onIonInput={(e) => set({ requirements: e.detail.value! })}
          rows={3}
          style={{ marginTop: 12 }}
        />

        <IonInput
          label="Skills (comma-separated)"
          placeholder="e.g. Plumbing, Electrical, Welding"
          value={form.skillsRequired}
          onIonInput={(e) => set({ skillsRequired: e.detail.value! })}
          style={{ marginTop: 12 }}
        />

        <IonSelect
          label="Province"
          value={form.province}
          onIonChange={(e) => set({ province: e.detail.value })}
          style={{ marginTop: 12 }}
        >
          {PROVINCES.map((p) => (
            <IonSelectOption key={p} value={p}>{p}</IonSelectOption>
          ))}
        </IonSelect>
        {errors.province && <IonLabel color="danger" className="ion-padding-start">{errors.province}</IonLabel>}

        <IonInput
          label="Location"
          placeholder="e.g. Johannesburg, Cape Town"
          value={form.location}
          onIonInput={(e) => set({ location: e.detail.value! })}
          style={{ marginTop: 12 }}
        />
        {errors.location && <IonLabel color="danger" className="ion-padding-start">{errors.location}</IonLabel>}

        <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
          <IonInput
            label="Min salary (ZAR)"
            type="number"
            value={form.salaryMin}
            onIonInput={(e) => set({ salaryMin: e.detail.value! })}
          />
          <IonInput
            label="Max salary (ZAR)"
            type="number"
            value={form.salaryMax}
            onIonInput={(e) => set({ salaryMax: e.detail.value! })}
          />
        </div>

        <IonSelect
          label="Work type"
          value={form.workType}
          onIonChange={(e) => set({ workType: e.detail.value })}
          style={{ marginTop: 12 }}
        >
          {workTypeEnum.map((w) => (
            <IonSelectOption key={w} value={w}>{w}</IonSelectOption>
          ))}
        </IonSelect>

        <IonInput
          label="Application deadline"
          type="date"
          value={form.applicationDeadline}
          onIonInput={(e) => set({ applicationDeadline: e.detail.value! })}
          style={{ marginTop: 12 }}
        />
      </div>

      <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
        <IonButton expand="block" onClick={handleSave} disabled={updateJob.isPending}>
          <IonIcon icon={save} slot="start" />
          {updateJob.isPending ? 'Saving…' : 'Save changes'}
        </IonButton>
        <IonButton expand="block" fill="outline" onClick={onCancel}>
          Cancel
        </IonButton>
      </div>

      <IonToast isOpen={!!toast} message={toast?.msg} color={toast?.color} duration={2000} onDidDismiss={() => setToast(null)} />
    </div>
  );
}
